import { client } from './amplifyClient';

/**
 * Notification Preferences Service
 * CRUD operations for user notification preferences
 */

export interface NotificationPreferencesInput {
  enabled?: boolean;
  pushEnabled?: boolean;
  emailEnabled?: boolean;
  smsEnabled?: boolean;
  inAppEnabled?: boolean;
  notifyOnVivid?: boolean;
  notifyOnGreat?: boolean;
  notifyOnGood?: boolean;
  notifyOnFair?: boolean;
  hoursBeforeSunrise?: number;
  hoursBeforeSunset?: number;
  eveningReminderEnabled?: boolean;
  eveningReminderTime?: string;
  maxNotificationsPerDay?: number;
  quietHoursStart?: string;
  quietHoursEnd?: string;
  notifyForAllLocations?: boolean;
  notifyForPrimaryOnly?: boolean;
  pushSubscription?: Record<string, unknown>;
  deviceTokens?: string[];
}

export class NotificationPreferencesServiceError extends Error {
  constructor(
    message: string,
    public code?: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'NotificationPreferencesServiceError';
  }
}

/**
 * Get notification preferences by userId
 * Returns null if preferences don't exist
 */
export async function getNotificationPreferences(userId: string) {
  try {
    const { data, errors } = await client.models.NotificationPreference.list({
      filter: { userId: { eq: userId } },
    });

    if (errors && errors.length > 0) {
      throw new NotificationPreferencesServiceError(
        'Failed to fetch notification preferences',
        'FETCH_ERROR',
        errors
      );
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    if (error instanceof NotificationPreferencesServiceError) {
      throw error;
    }
    throw new NotificationPreferencesServiceError(
      'Unexpected error fetching notification preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Create notification preferences with defaults
 */
export async function createNotificationPreferences(
  userId: string,
  preferences: Partial<NotificationPreferencesInput> = {}
) {
  try {
    const existing = await getNotificationPreferences(userId);
    if (existing) {
      throw new NotificationPreferencesServiceError(
        'Notification preferences already exist',
        'ALREADY_EXISTS'
      );
    }

    const { data, errors } = await client.models.NotificationPreference.create(
      {
        userId,
        enabled: preferences.enabled ?? true,
        pushEnabled: preferences.pushEnabled ?? true,
        emailEnabled: preferences.emailEnabled ?? false,
        smsEnabled: preferences.smsEnabled ?? false,
        inAppEnabled: preferences.inAppEnabled ?? true,
        notifyOnVivid: preferences.notifyOnVivid ?? true,
        notifyOnGreat: preferences.notifyOnGreat ?? true,
        notifyOnGood: preferences.notifyOnGood ?? false,
        notifyOnFair: preferences.notifyOnFair ?? false,
        hoursBeforeSunrise: preferences.hoursBeforeSunrise ?? 2,
        hoursBeforeSunset: preferences.hoursBeforeSunset ?? 2,
        eveningReminderEnabled: preferences.eveningReminderEnabled ?? false,
        eveningReminderTime: preferences.eveningReminderTime,
        maxNotificationsPerDay: preferences.maxNotificationsPerDay ?? 4,
        quietHoursStart: preferences.quietHoursStart,
        quietHoursEnd: preferences.quietHoursEnd,
        notifyForAllLocations: preferences.notifyForAllLocations ?? false,
        notifyForPrimaryOnly: preferences.notifyForPrimaryOnly ?? true,
        pushSubscription: preferences.pushSubscription,
        deviceTokens: preferences.deviceTokens,
      },
    );

    if (errors && errors.length > 0) {
      throw new NotificationPreferencesServiceError(
        'Failed to create notification preferences',
        'CREATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new NotificationPreferencesServiceError(
        'No data returned after creating notification preferences',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof NotificationPreferencesServiceError) {
      throw error;
    }
    throw new NotificationPreferencesServiceError(
      'Unexpected error creating notification preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Update existing notification preferences
 */
export async function updateNotificationPreferences(
  userId: string,
  updates: Partial<NotificationPreferencesInput>
) {
  try {
    const existing = await getNotificationPreferences(userId);

    if (!existing) {
      throw new NotificationPreferencesServiceError(
        'Notification preferences not found. Create preferences first.',
        'NOT_FOUND'
      );
    }

    const { data, errors } = await client.models.NotificationPreference.update(
      {
        id: existing.id,
        ...updates,
      },
    );

    if (errors && errors.length > 0) {
      throw new NotificationPreferencesServiceError(
        'Failed to update notification preferences',
        'UPDATE_ERROR',
        errors
      );
    }

    if (!data) {
      throw new NotificationPreferencesServiceError(
        'No data returned after updating notification preferences',
        'NO_DATA'
      );
    }

    return data;
  } catch (error) {
    if (error instanceof NotificationPreferencesServiceError) {
      throw error;
    }
    throw new NotificationPreferencesServiceError(
      'Unexpected error updating notification preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}

/**
 * Delete notification preferences
 */
export async function deleteNotificationPreferences(userId: string) {
  try {
    const existing = await getNotificationPreferences(userId);

    if (!existing) {
      throw new NotificationPreferencesServiceError(
        'Notification preferences not found',
        'NOT_FOUND'
      );
    }

    const { data, errors } = await client.models.NotificationPreference.delete({
      id: existing.id,
    });

    if (errors && errors.length > 0) {
      throw new NotificationPreferencesServiceError(
        'Failed to delete notification preferences',
        'DELETE_ERROR',
        errors
      );
    }

    return data;
  } catch (error) {
    if (error instanceof NotificationPreferencesServiceError) {
      throw error;
    }
    throw new NotificationPreferencesServiceError(
      'Unexpected error deleting notification preferences',
      'UNKNOWN_ERROR',
      error
    );
  }
}
